/**
 * Issue #221 / #345 — 课程目录的高级搜索 / 排序 / 列隐藏 / 分页（纯逻辑）。
 *
 * Issue #345 起：分页 / 搜索 / 排序全部下沉到后端 GET /admin/courses（page/pageSize/
 * keyword/status/sortBy/sortOrder → { items, pagination }）。本模块只保留：
 *   - 列隐藏（纯展示层）语义：`name` / `actions` 不可隐藏，其余可隐藏，可见列按白名单顺序稳定排序；
 *   - 筛选状态（keyword/status/sortBy/sortOrder/page/pageSize）到后端查询参数的映射。
 * 不再在客户端拉全量后本地过滤 / 分页。
 */
import type { CourseStatus } from './types';

export interface CourseColumnMeta {
  key: string;
  label: string;
  hideable: boolean;
  defaultVisible: boolean;
}

/** 课程目录列白名单（顺序即展示顺序）。 */
export const COURSE_COLUMNS: readonly CourseColumnMeta[] = [
  { key: 'name', label: '课程名称', hideable: false, defaultVisible: true },
  { key: 'code', label: '课程编码', hideable: true, defaultVisible: true },
  { key: 'questionCount', label: '题量', hideable: true, defaultVisible: true },
  // Issue #380（返修 #342）：分段配置状态列（未配置 / 已配置 N 个批次）。
  { key: 'segments', label: '分段', hideable: true, defaultVisible: true },
  { key: 'status', label: '状态', hideable: true, defaultVisible: true },
  { key: 'sortOrder', label: '排序值', hideable: true, defaultVisible: false },
  { key: 'updatedAt', label: '更新时间', hideable: true, defaultVisible: true },
  { key: 'actions', label: '操作', hideable: false, defaultVisible: true },
] as const;

export const COURSE_PAGE_SIZE_OPTIONS = [20, 50, 100] as const;
export const COURSE_DEFAULT_PAGE_SIZE = 20;

export type CourseStatusFilter = 'ALL' | CourseStatus;
export type CourseSortBy = 'name' | 'questionCount' | 'sortOrder' | 'updatedAt';
export type SortOrder = 'asc' | 'desc';

export interface CourseCatalogFilterState {
  keyword: string;
  status: CourseStatusFilter;
  sortBy: CourseSortBy;
  sortOrder: SortOrder;
  page: number;
  pageSize: number;
}

export const DEFAULT_COURSE_FILTER: CourseCatalogFilterState = {
  keyword: '',
  status: 'ALL',
  sortBy: 'sortOrder',
  sortOrder: 'asc',
  page: 1,
  pageSize: COURSE_DEFAULT_PAGE_SIZE,
};

/** 后端 GET /admin/courses 查询参数（Issue #345）。 */
export interface CourseListQuery {
  page: number;
  pageSize: number;
  keyword?: string;
  status?: CourseStatusFilter;
  sortBy: CourseSortBy;
  sortOrder: SortOrder;
}

/** 可见列：默认集来自 COURSE_COLUMNS.defaultVisible；强制包含不可隐藏列，按白名单顺序。 */
export function resolveCourseColumns(preferred: string[] | null | undefined): string[] {
  const order = COURSE_COLUMNS.map((c) => c.key);
  const whitelist = new Set(order);
  const nonHideable = new Set(COURSE_COLUMNS.filter((c) => !c.hideable).map((c) => c.key));
  const base =
    preferred && preferred.length > 0
      ? preferred
      : COURSE_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key);
  const baseSet = new Set(base.filter((k) => whitelist.has(k)));
  return order.filter((k) => baseSet.has(k) || nonHideable.has(k));
}

/** 切换列显隐：仅白名单内且可隐藏的列可切换；其余原样返回。 */
export function toggleCourseColumn(current: string[], key: string): string[] {
  const meta = COURSE_COLUMNS.find((c) => c.key === key);
  if (!meta || !meta.hideable) return current;
  if (current.includes(key)) return current.filter((c) => c !== key);
  const order = COURSE_COLUMNS.map((c) => c.key);
  return order.filter((c) => current.includes(c) || c === key);
}

export function normalizeCoursePageSize(value: number): number {
  return (COURSE_PAGE_SIZE_OPTIONS as readonly number[]).includes(value)
    ? value
    : COURSE_DEFAULT_PAGE_SIZE;
}

/**
 * Issue #345：把课程目录筛选状态映射为后端查询参数。keyword 去空白后为空则省略；
 * status=ALL 省略（后端不过滤）；pageSize 归一化到白名单；page 至少为 1。
 */
export function buildCourseListQuery(state: CourseCatalogFilterState): CourseListQuery {
  const keyword = state.keyword.trim();
  return {
    page: Math.max(1, Math.trunc(state.page) || 1),
    pageSize: normalizeCoursePageSize(state.pageSize),
    keyword: keyword.length > 0 ? keyword : undefined,
    status: state.status === 'ALL' ? undefined : state.status,
    sortBy: state.sortBy,
    sortOrder: state.sortOrder,
  };
}

/** 计算分页总页数（后端 total → 页数；用于禁用「下一页」）。 */
export function courseTotalPages(total: number, pageSize: number): number {
  const size = normalizeCoursePageSize(pageSize);
  return total <= 0 ? 1 : Math.ceil(total / size);
}
